import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * **M30 — C-I18N**: locale, currency and timezone, supplied once and read everywhere.
 *
 * ── The gap this closes, and why it is not cosmetic ───────────────────────────────────────────
 *
 * ⚠️ **[BR-10](../../../docs/platform/06-prd.md) has required "all timestamps stored UTC; user-facing
 * times in the ORG timezone" since it was written, and there was no org timezone to read.** Every
 * `Timestamp` fell back to the *browser's*, so two colleagues looking at the same audit event saw
 * different times and neither was told why. For an append-only trail whose whole value is agreeing
 * about when something happened, that is precisely the wrong disagreement.
 *
 * ⚠️ **An inferred timezone is LABELLED as inferred.** `null` from the org means nobody has set one —
 * not UTC, and not "whatever this browser says". The formatter falls back to the browser because that
 * is what a person expects to read, and `inferred` is true so a surface can say so. Presenting a
 * guess as the organisation's setting is the failure this distinction exists to prevent.
 *
 * ── Why the plumbing ships before the content ─────────────────────────────────────────────────
 *
 * ⚠️ **English-only is the decision** ([Q26](../../../docs/process/discovery-2026-07-26-open-questions.md)),
 * and the *plumbing* is built now anyway — because the expensive half of localisation is the
 * plumbing, and retrofitting it across every screen later is the one strictly more expensive option.
 * `locale` therefore threads through every formatter from day one even though only one value is ever
 * passed.
 */
export interface Formatting {
  /** BCP-47. `undefined` lets `Intl` use the runtime's own, which is right for a visitor. */
  locale?: string;
  /** IANA name. */
  timeZone: string;
  /**
   * ⚠️ True when `timeZone` came from the browser rather than from the organisation. A surface
   * showing a time in an inferred zone should be able to say so; one that cannot will imply the
   * organisation chose it.
   */
  inferred: boolean;
  /** ISO-4217, for `Money`. `null` where the org has set no currency — never a substituted default. */
  currency: string | null;
}

const FALLBACK: Formatting = {
  locale: undefined,
  /* Resolved once, at module scope: `Intl` is not free and this is read by every timestamp on a
     screen. `?? 'UTC'` because a locked-down runtime can return an empty string. */
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  inferred: true,
  currency: null,
};

const FormattingContext = createContext<Formatting>(FALLBACK);

/**
 * Supply the org's formatting.
 *
 * ⚠️ **Takes `timeZone` as `string | null | undefined`** deliberately: the caller passes what the
 * organisation actually has, and the *provider* decides what an absent one means — rather than every
 * call site inventing its own fallback, which is how two screens end up disagreeing about the same
 * unset value.
 */
export function FormattingProvider({
  locale,
  timeZone,
  currency = null,
  children,
}: {
  locale?: string;
  timeZone?: string | null;
  currency?: string | null;
  children: ReactNode;
}) {
  const value = useMemo<Formatting>(
    () => ({
      locale,
      timeZone: timeZone || FALLBACK.timeZone,
      inferred: !timeZone,
      currency,
    }),
    [locale, timeZone, currency]
  );
  return <FormattingContext.Provider value={value}>{children}</FormattingContext.Provider>;
}

/**
 * Read the current formatting.
 *
 * ⚠️ **Never throws when there is no provider.** A component that renders a date must not depend on
 * an ancestor existing — the guest remediation workspace has no shell at all, and a test rendering
 * one component in isolation is the normal case rather than the exception.
 */
export function useFormatting(): Formatting {
  return useContext(FormattingContext);
}
